<template>
    <div ref="chartEl" class="stl-chart-el"></div>
</template>

<script>
import { stlChartTextStyle } from "../../utils/settlementFormat.js";
import {
    CHART_AXIS,
    CHART_PALETTE,
    baseChartOption,
    dailyBarItemStyle,
} from "../../utils/chartTheme.js";
import * as echarts from "echarts";

export default {
    name: "DailyUsageChart",
    props: {
        labels: { type: Array, default: () => [] },
        data: { type: Array, default: () => [] },
        unit: { type: String, default: "kWh" },
        title: { type: String, default: "" },
        /** 에너지원별 색 — 미지정 시 기본 저채도 블루 */
        color: { type: String, default: "" },
    },
    data() {
        return { chart: null, ro: null };
    },
    watch: {
        labels: { handler: "render", deep: true },
        data: { handler: "render", deep: true },
        unit: "render",
        title: "render",
        color: "render",
    },
    mounted() {
        this.render();
        window.addEventListener("resize", this.resize);
        if (typeof ResizeObserver !== "undefined" && this.$refs.chartEl) {
            this.ro = new ResizeObserver(() => this.resize());
            this.ro.observe(this.$refs.chartEl);
        }
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.resize);
        if (this.ro) this.ro.disconnect();
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
    },
    methods: {
        barColor() {
            return this.color || CHART_PALETTE.primary;
        },
        render() {
            if (!this.$refs.chartEl || !this.labels.length) {
                if (this.chart) this.chart.clear();
                return;
            }
            if (!this.chart) this.chart = echarts.init(this.$refs.chartEl);
            const ts = (extra) => stlChartTextStyle(extra);
            const base = baseChartOption(ts);
            const fill = dailyBarItemStyle(this.barColor());
            const hoverColor = this.color
                ? this.color.startsWith("rgba")
                    ? this.color.replace(/[\d.]+\)$/, "0.82)")
                    : `${this.color}D0`
                : CHART_PALETTE.dailyBarHover;

            const n = this.labels.length;
            const rotate = n > 14;
            const gridBottom = rotate ? 52 : 40;

            this.chart.setOption({
                ...base,
                color: [this.barColor()],
                textStyle: ts(),
                grid: {
                    left: 44,
                    right: 12,
                    top: this.title ? 36 : 28,
                    bottom: gridBottom,
                },
                tooltip: {
                    trigger: "axis",
                    backgroundColor: "rgba(255,255,255,0.96)",
                    borderColor: CHART_AXIS.axisLine,
                    textStyle: ts({ fontSize: 12, color: CHART_AXIS.label }),
                    valueFormatter: (v) => `${Number(v).toLocaleString()} ${this.unit}`,
                },
                title: this.title
                    ? {
                          text: this.title,
                          left: 0,
                          top: 0,
                          textStyle: ts({ fontSize: 12, fontWeight: 600, color: CHART_AXIS.label }),
                      }
                    : undefined,
                xAxis: {
                    type: "category",
                    data: this.labels,
                    ...base.xAxis,
                    axisLabel: {
                        ...ts({ fontSize: 10, color: CHART_AXIS.label }),
                        rotate: rotate ? 45 : 0,
                        margin: rotate ? 12 : 8,
                    },
                },
                yAxis: {
                    type: "value",
                    name: this.unit,
                    ...base.yAxis,
                },
                series: [
                    {
                        name: "사용량",
                        type: "bar",
                        barMaxWidth: 14,
                        itemStyle: fill,
                        emphasis: {
                            itemStyle: { color: hoverColor, borderRadius: [3, 3, 0, 0] },
                        },
                        data: this.data,
                    },
                ],
            });
            this.$nextTick(() => this.resize());
        },
        resize() {
            if (this.chart) this.chart.resize();
        },
    },
};
</script>

<style scoped>
.stl-chart-el {
    width: 100%;
    height: 280px;
    min-height: 240px;
}
</style>
